<template>
  <ErrorBoundary :module-name="`WidgetsArea ${area}`">
    <div
      v-if="isShowWidgetsArea"
      :class="classWidgetArea"
      class="widgets-area"
    >
      <component
        :is="containerWidgetList"
        :widgets="widgetsFiltered"
      />
    </div>
  </ErrorBoundary>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import WidgetList from '~/components/Widgets/WidgetList.vue';
  import StickyList from '~/components/StickyList.vue';
  import type { IWidgetAttributes, TContent } from '~/types/common';
  import { useDeviceStore } from '~/store/device';
  import { useWidgetsStore } from '~/store/widgets';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import ErrorBoundary from '@devhacker/shared/components/ErrorBoundary.vue';
  import { addIndexToAdfoxWidgets } from '@devhacker/shared/utils/adfox';

  export default defineNuxtComponent({
    name: 'WidgetsArea',

    components: {
      WidgetList,
      StickyList,
      ErrorBoundary,
    },

    // при необходимости в нужном виджете можем получить его "место обитания"
    provide() {
      return {
        area: () => this.area,
        // для аналитики
        areaAnalytics: () => {
          const analyticsArea: Record<string, string> = {
            'above-menu': '_над меню',
            'below-menu': '_под меню',
            'above-comments': '_над комментариями',
            'below-comments': '_под комментариями',
            'after-first-paragraph': '_после 1 абзаца',
            'dynamic-post': '_динамическая зона в посте',
          };

          return analyticsArea[this.area] ?? '';
        },
      };
    },

    props: {
      area: {
        type: String as PropType<string>,
        default: '',
      },

      containerWidgetList: {
        type: String as PropType<string>,
        default: 'WidgetList',
      },
      indexSlicedContent: {
        type: Number,
        default: 0,
      },
    },

    computed: {
      widgets() {
        const widgetsStore = useWidgetsStore();
        switch (this.area) {
          case 'sidebar':
            return widgetsStore.sidebarWidgets;
          case 'home':
            return widgetsStore.homeWidgets;
          case 'above-menu':
            return widgetsStore.aboveMenuWidgets;
          case 'below-menu':
            return widgetsStore.belowMenuWidgets;
          case 'above-comments':
            return widgetsStore.aboveCommentsWidgets;
          case 'below-comments':
            return widgetsStore.belowCommentsWidgets;
          case 'after-first-paragraph':
            return widgetsStore.afterFirstParagraphWidgets;
          case 'post-dynamic':
            return widgetsStore.postDynamic;
          case 'scripts':
            return widgetsStore.scriptsWidgets;
          default:
            return [];
        }
      },

      deviceType() {
        return useDeviceStore().deviceType;
      },

      isShowWidgetsArea(): boolean {
        return this.widgetsFiltered.length > 0;
      },

      article() {
        return useSingleArticleStore().article;
      },

      classWidgetArea(): string | null {
        return this.area ? `widgets-area--${this.area}` : null;
      },

      widgetsFiltered(): TContent[] {
        const { area, widgets } = this;
        const filterByArea: Record<string, (widgets: TContent[]) => TContent[]> = {
          'below-menu': this.widgetsFilteredBelowMenu,
          'post-dynamic': this.widgetsPostDynamic,
        };
        const widgetsByArea = filterByArea[area] ? filterByArea[area](widgets) : widgets;

        const filtered = widgetsByArea.filter(this.filterByDevice(this.deviceType));

        return filtered;
      },
    },

    methods: {
      widgetsFilteredBelowMenu(widgets: TContent[]): TContent[] {
        const { article } = this;
        // убрать отображение брендирование для шаблона Широкоформатный
        // https://app.clickup.com/t/2uq9n94
        return widgets.filter((widget: TContent) => {
          return article?.template === 'broadside'
            ? !widget.attributes?.title?.startsWith('adfox brand')
            : true;
        });
      },

      widgetsPostDynamic(postDynamic: TContent[]): TContent[] {
        const widgets = addIndexToAdfoxWidgets(postDynamic, this.indexSlicedContent, 'children');
        return widgets;
      },

      filterByDevice(
        deviceTypeDetect: 'mobile' | 'tablet' | 'desktop',
      ): (widget: TContent) => boolean {
        return (widget: TContent) => {
          const attributes: IWidgetAttributes = widget.attributes;
          return attributes.deviceType?.length
            ? attributes.deviceType?.includes(deviceTypeDetect)
            : true;
        };
      },
    },
  });
</script>

<style lang="scss">
  .widgets-area {
    $widgetArea: &;
    $above-article-margin--mobile: $spacing-8;
    $above-article-margin--dekstop: $spacing-10;
    $default-margin: $spacing-6;
    $home-margin: $spacing-6;

    margin: 24px 0;

    .widget {
      margin-left: auto;
      margin-right: auto;
      @include tablet {
        margin-left: 0;
        margin-right: 0;
      }

      &:not(:last-child) {
        margin-bottom: $default-margin;
      }
    }

    &--home {
      .widgets-list {
        .widget {
          margin: $home-margin auto;
        }
      }
    }

    &--sidebar {
      display: flex;
      height: 100%;

      .widgets-list {
        .widget--bh-kiss__html:first-child {
          .widget-html--adfox-empty {
            margin-top: -$default-margin;
          }
        }
      }
    }

    &--below-menu,
    &--above-menu {
      &#{$widgetArea} {
        .widgets-list {
          .widget {
            margin: 0;
          }
        }
      }
    }

    &--above-comments,
    &--below-comments {
      margin: $above-article-margin--mobile 0;
      @include tablet {
        margin: $above-article-margin--dekstop 0;
      }

      .widget:not(:last-child) {
        margin-bottom: $spacing-10;
      }
    }

    &--above-comments,
    &--below-comments {
      &:last-child {
        .widget--bh-kiss__html:last-child {
          .widget-html--adfox-empty {
            margin-top: -$above-article-margin--mobile;
            @include tablet {
              margin-top: -$above-article-margin--dekstop;
            }
          }
        }
      }
    }
  }
</style>
